import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getAllTickets, 
  getTicketDetails, 
  addMessageToTicket, 
  updateTicketStatus, 
  getTicketsCountByStatus,
  addInternalNoteToTicket,
  TICKET_STATUS 
} from '../services/supportService';

// Clés de requête pour React Query
const TICKETS_KEY = 'tickets';
const TICKET_DETAIL_KEY = 'ticketDetail';
const TICKETS_COUNT_KEY = 'ticketsCount';

/**
 * Hook personnalisé pour gérer les tickets de support
 * @param {Object} filterOptions - Options de filtrage des tickets
 * @param {number} limit - Nombre de tickets à récupérer
 * @returns {Object} Méthodes et données pour manipuler les tickets
 */
export const useTickets = (filterOptions = {}, limit = 20) => {
  const queryClient = useQueryClient();
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  
  // Récupérer la liste des tickets
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [TICKETS_KEY, filterOptions, limit],
    queryFn: async () => {
      const result = await getAllTickets({
        ...filterOptions,
        limit,
        lastVisible: null // Commencer toujours par la première page lors d'un changement de filtre
      });
      
      setLastVisible(result.lastVisible);
      setHasMore(result.tickets.length === limit);
      
      return result.tickets;
    }
  });
  
  // Récupérer plus de tickets (pagination)
  const fetchMoreTickets = useCallback(async () => {
    if (!lastVisible || !hasMore) return;
    
    try {
      const result = await getAllTickets({
        ...filterOptions,
        limit,
        lastVisible
      });
      
      setLastVisible(result.lastVisible);
      setHasMore(result.tickets.length === limit);
      
      // Mettre à jour les données en cache
      queryClient.setQueryData([TICKETS_KEY, filterOptions, limit], (oldData = []) => {
        return [...oldData, ...result.tickets];
      });
      
      return result.tickets;
    } catch (error) {
      console.error('Erreur lors du chargement de tickets supplémentaires:', error);
      throw error;
    }
  }, [lastVisible, hasMore, filterOptions, limit, queryClient]);
  
  // Compter les tickets par statut
  const { 
    data: ticketsCount, 
    isLoading: isLoadingCount 
  } = useQuery({
    queryKey: [TICKETS_COUNT_KEY],
    queryFn: getTicketsCountByStatus
  });
  
  // Mutation pour répondre à un ticket
  const replyToTicketMutation = useMutation({
    mutationFn: ({ userId, ticketId, content, staffName }) => 
      addMessageToTicket(userId, ticketId, content, staffName),
    onSuccess: (_, variables) => {
      // Invalider le cache pour forcer un rechargement des données
      queryClient.invalidateQueries({ queryKey: [TICKET_DETAIL_KEY, variables.userId, variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY] });
    }
  });
  
  // Mutation pour mettre à jour le statut d'un ticket
  const updateStatusMutation = useMutation({
    mutationFn: ({ userId, ticketId, status }) => 
      updateTicketStatus(userId, ticketId, status),
    onSuccess: (_, variables) => {
      // Invalider le cache pour forcer un rechargement des données
      queryClient.invalidateQueries({ queryKey: [TICKET_DETAIL_KEY, variables.userId, variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TICKETS_COUNT_KEY] });
    }
  });
  
  return {
    tickets: data || [],
    isLoading,
    isError,
    error,
    hasMore,
    fetchMoreTickets,
    ticketsCount: ticketsCount || {
      all: 0,
      [TICKET_STATUS.NEW]: 0,
      [TICKET_STATUS.IN_PROGRESS]: 0,
      [TICKET_STATUS.RESOLVED]: 0,
      [TICKET_STATUS.CLOSED]: 0
    },
    isLoadingCount,
    replyToTicket: replyToTicketMutation.mutate,
    isReplying: replyToTicketMutation.isPending,
    updateStatus: updateStatusMutation.mutate,
    isUpdatingStatus: updateStatusMutation.isPending,
    refetchTickets: refetch
  };
};

/**
 * Hook personnalisé pour gérer un ticket spécifique
 * @param {string} userId - ID de l'utilisateur propriétaire du ticket
 * @param {string} ticketId - ID du ticket
 * @returns {Object} Données et méthodes pour manipuler le ticket
 */
export const useTicketDetail = (userId, ticketId) => {
  const queryClient = useQueryClient();
  
  // Récupérer les détails d'un ticket
  const { data: ticket, isLoading, isError, error } = useQuery({
    queryKey: [TICKET_DETAIL_KEY, userId, ticketId],
    queryFn: () => getTicketDetails(userId, ticketId),
    enabled: !!userId && !!ticketId
  });
  
  // Mutation pour répondre au ticket
  const replyMutation = useMutation({
    mutationFn: ({ content, staffName }) => 
      addMessageToTicket(userId, ticketId, content, staffName),
    onSuccess: () => {
      // Invalider le cache pour forcer un rechargement des données
      queryClient.invalidateQueries({ queryKey: [TICKET_DETAIL_KEY, userId, ticketId] });
    }
  });
  
  // Mutation pour mettre à jour le statut du ticket
  const updateStatusMutation = useMutation({
    mutationFn: (status) => 
      updateTicketStatus(userId, ticketId, status),
    onSuccess: () => {
      // Invalider le cache pour forcer un rechargement des données
      queryClient.invalidateQueries({ queryKey: [TICKET_DETAIL_KEY, userId, ticketId] });
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TICKETS_COUNT_KEY] });
    }
  });
  
  // Mutation pour ajouter une note interne
  const addNoteMutation = useMutation({
    mutationFn: (noteData) => {
      // Si noteData est une string, le convertir en objet avec content
      const noteContent = typeof noteData === 'string' ? noteData : noteData.content;
      const adminName = typeof noteData === 'object' && noteData.adminName 
        ? noteData.adminName 
        : 'Admin MSD';
      
      return addInternalNoteToTicket(userId, ticketId, noteContent, adminName);
    },
    onSuccess: (result, variables, context) => {
      // Au lieu d'invalider la requête, on va mettre à jour les données en place
      // Récupérer les données actuelles du cache
      const currentData = queryClient.getQueryData([TICKET_DETAIL_KEY, userId, ticketId]);
      
      if (currentData) {
        // Créer une copie avec la nouvelle note
        const updatedData = {
          ...currentData,
          internalNotes: [
            ...(currentData.internalNotes || []),
            {
              content: typeof variables === 'string' ? variables : variables.content,
              adminName: typeof variables === 'object' && variables.adminName 
                ? variables.adminName 
                : 'Admin MSD',
              timestamp: new Date() // Ajouter un timestamp local temporaire
            }
          ]
        };
        
        // Mettre à jour le cache directement sans déclencher de requête
        queryClient.setQueryData([TICKET_DETAIL_KEY, userId, ticketId], updatedData);
      }
      
      // Marquer la requête comme périmée mais ne pas la recharger immédiatement
      queryClient.invalidateQueries({ 
        queryKey: [TICKET_DETAIL_KEY, userId, ticketId],
        refetchActive: false
      });
    }
  });
  
  return {
    ticket,
    isLoading,
    isError,
    error,
    reply: replyMutation.mutate,
    isReplying: replyMutation.isPending,
    addInternalNote: addNoteMutation.mutate,
    isAddingNote: addNoteMutation.isPending,
    updateStatus: updateStatusMutation.mutate,
    isUpdatingStatus: updateStatusMutation.isPending
  };
};